import { FORM_DRAFT_SCHEMA_VERSION, FormDraftConfig, FormField, FormFieldOption, FormFieldType, FormSection, getOnlyActiveWhenOperators, readFormConfig } from '@/modules/forms/formConfig';
export * from '@/modules/forms/formConfig';

/** Form content that Interface authors before Platform creates the Form. */
export interface FormDraftInput {
  name: string;
  description: string;
  draftConfig: FormDraftConfig;
}

/** A persisted Form with read-only metadata supplied by Platform. */
export interface FormDraft extends FormDraftInput {
  readonly id: string;
  readonly draftSchemaVersion: typeof FORM_DRAFT_SCHEMA_VERSION;
  readonly currentPublishedVersion: Readonly<{
    id: string;
    version: number;
  }> | null;
  readonly updatedAt: string;
}
export type FormStarter = 'scratch' | 'trustReview';
export function updateFormField(field: FormField, patch: Partial<Omit<FormField, 'id' | 'type'>>): FormField {
  const updatedField = {
    ...field,
    ...patch,
    ...(patch.options !== undefined ? {
      children: remapOptionConditions(field, patch.options),
      exports: reconcileOptionTranslations(field, patch.options)
    } : {})
  };
  const {
    options: _options,
    validation: _validation,
    dependsOn,
    ...baseField
  } = updatedField;
  // Platform rejects dependsOn on object and array questions.
  const keepsDependsOn = dependsOn !== undefined && canDeclareDependsOn(updatedField, false);
  let options: FormFieldOption[] | undefined;
  if (isSelectFieldType(updatedField.fieldType)) {
    options = updatedField.options?.length ? updatedField.options : [{
      label: '',
      value: ''
    }];
  }
  const validation = isTextFieldType(updatedField.fieldType) ? updatedField.validation : undefined;
  return {
    ...baseField,
    ...(options ? {
      options
    } : {}),
    ...(validation ? {
      validation
    } : {}),
    ...(keepsDependsOn ? {
      dependsOn
    } : {})
  };
}
function reconcileOptionTranslations(field: FormField, nextOptions: FormFieldOption[]): FormField['exports'] {
  const salesforce = field.exports?.salesforce;
  if (!salesforce || !field.options) return field.exports;
  const oldValues = new Set(field.options.map(option => option.value));
  const nextValues = new Set(nextOptions.map(option => option.value));
  const renamedValues = new Map<string, string>();
  if (field.options.length === nextOptions.length) {
    field.options.forEach((option, index) => {
      const nextValue = nextOptions[index]?.value;
      if (nextValue !== undefined && !nextValues.has(option.value) && !oldValues.has(nextValue)) {
        renamedValues.set(option.value, nextValue);
      }
    });
  }
  return {
    salesforce: {
      ...salesforce,
      sentAs: salesforce.sentAs.flatMap(translation => {
        if (nextValues.has(translation.formValue)) return [translation];
        const renamedValue = renamedValues.get(translation.formValue);
        return renamedValue !== undefined ? [{
          ...translation,
          formValue: renamedValue
        }] : [];
      })
    }
  };
}
function remapOptionConditions(field: FormField, nextOptions: FormFieldOption[] | undefined): FormField[] | undefined {
  if (!field.children || !nextOptions || !field.options || nextOptions.length !== field.options.length) {
    return field.children;
  }
  const changedValues = new Map<string, string>(field.options.flatMap((option, index) => {
    const nextValue = nextOptions[index]?.value;
    return nextValue !== undefined && nextValue !== option.value ? [[option.value, nextValue]] as const : [];
  }));
  if (changedValues.size === 0) return field.children;
  return field.children.map(child => {
    const condition = child.onlyActiveWhen;
    if (condition?.fieldId !== field.id || typeof condition.candidateValue !== 'string' || !changedValues.has(condition.candidateValue)) {
      return child;
    }
    return {
      ...child,
      onlyActiveWhen: {
        ...condition,
        candidateValue: changedValues.get(condition.candidateValue)!
      }
    };
  });
}
export function createBlankField(): FormField {
  return {
    id: globalThis.crypto.randomUUID(),
    type: 'field',
    fieldType: 'shortText',
    label: '',
    export: {
      includeReasoning: true,
      includeQuotes: true,
      includeCitations: true
    }
  };
}

/** Appends a blank question that is active for one single-select answer. */
export function addSingleSelectFollowUp(sections: FormSection[], parentId: string, candidateValue: string, followUp: FormField): FormSection[] {
  const parent = findFieldLocation(sections, parentId);
  if (!parent || parent.parentIndex !== undefined || parent.field.fieldType !== 'singleSelect' || !(parent.field.options ?? []).some(option => option.value === candidateValue)) {
    return sections;
  }
  const conditionalFollowUp = {
    ...withFieldChildren(followUp, []),
    onlyActiveWhen: {
      fieldId: parentId,
      operator: 'equals' as const,
      candidateValue
    }
  };
  const section = sections[parent.sectionIndex]!;
  const nextChildren = [...section.children];
  nextChildren[parent.fieldIndex] = {
    ...parent.field,
    children: [...(parent.field.children ?? []), conditionalFollowUp]
  };
  return replaceSectionChildren(sections, section.id, nextChildren);
}
export function createBlankSection(): FormSection {
  return {
    id: globalThis.crypto.randomUUID(),
    type: 'section',
    label: '',
    children: []
  };
}
function createStarterField(label: string, fieldType: FormFieldType, extractionPrompt: string, options: string[] = []): FormField {
  const fieldOptions = options.map(option => ({
    label: option,
    value: option
  }));
  return {
    ...createBlankField(),
    label,
    fieldType,
    ...(fieldOptions.length > 0 ? {
      options: fieldOptions
    } : {}),
    extraction: {
      prompt: extractionPrompt
    }
  };
}
function createStarterSection(label: string, children: FormField[]): FormSection {
  return {
    ...createBlankSection(),
    label,
    children
  };
}
function createTrustReviewSections(): FormSection[] {
  return [createStarterSection('Trust information', [createStarterField('Trust name', 'shortText', "Identify the trust's formal name."), createStarterField('Current trustees', 'longText', 'Summarize who currently serves as trustee.'), createStarterField('Fiduciary capacity', 'singleSelect', 'Identify the capacity in which the current fiduciary serves.', ['Individual', 'Corporate', 'Co-trustees']), createStarterField('Grantor trust status', 'singleSelect', 'Determine whether the trust is treated as a grantor trust.', ['Yes', 'No']), createStarterField('Trust type', 'singleSelect', 'Classify the trust using the available options.', ['Revocable', 'Irrevocable', 'Testamentary'])]), createStarterSection('Administrative provisions', [createStarterField('Trustee resignation allowed', 'singleSelect', 'Determine whether the document permits a trustee to resign.', ['Yes', 'No']), createStarterField('Anticipated tax situs', 'shortText', 'Identify the expected jurisdiction for trust taxation.'), createStarterField('Waiver of objection included', 'singleSelect', 'Determine whether the trust includes a waiver of objection.', ['Yes', 'No'])]), createStarterSection('Upon death', [createStarterField('Upon-death summary', 'longText', "Summarize the main trust directions that apply after the grantor's death.")])];
}
export function getFormFields(sections: FormSection[]): FormField[] {
  const visit = (field: FormField): FormField[] => [field, ...(field.properties ?? []).flatMap(visit), ...(field.items ? visit(field.items) : []), ...(field.children ?? []).flatMap(visit)];
  return sections.flatMap(section => section.children.flatMap(visit));
}
export function getMaxLengthError(field: FormField): string | undefined {
  const maxLength = field.validation?.maxLength;
  if (!isTextFieldType(field.fieldType) || maxLength === undefined) {
    return undefined;
  }
  if (!Number.isInteger(maxLength) || maxLength <= 0) {
    return 'Enter a whole number greater than 0.';
  }
  return undefined;
}
export function getFieldOptionsError(field: FormField): string | undefined {
  if (!isSelectFieldType(field.fieldType)) return undefined;
  const options = field.options ?? [];
  if (options.length === 0) return 'Add at least one option.';
  if (options.some(option => !option.label.trim() || !option.value.trim())) {
    return 'Enter a label and value for every option.';
  }
  const values = options.map(option => option.value.trim());
  return new Set(values).size === values.length ? undefined : 'Option values must be unique.';
}
export function getOnlyActiveWhenError(field: FormField, parent?: FormField): string | undefined {
  const condition = field.onlyActiveWhen;
  if (!parent) {
    return condition ? 'A conditional question must be nested under its source question.' : undefined;
  }
  if (!condition) return 'Configure when this nested question is active.';
  if (condition.fieldId !== parent.id) {
    return 'A nested question must depend on its parent question.';
  }
  const operators = getOnlyActiveWhenOperators(parent.fieldType);
  if (operators.length === 0) {
    return 'The parent question type does not support conditional activation.';
  }
  if (!condition.operator || !operators.includes(condition.operator)) {
    return 'Select an operator supported by the parent question.';
  }
  if (parent.fieldType === 'shortText' || parent.fieldType === 'longText') {
    return condition.candidateValue === null ? undefined : 'Text presence conditions do not use a candidate value.';
  }
  return typeof condition.candidateValue === 'string' && (parent.options ?? []).some(option => option.value === condition.candidateValue) ? undefined : 'Select one of the parent question options.';
}
export function reorderSections(sections: FormSection[], oldIndex: number, newIndex: number): FormSection[] {
  if (oldIndex === newIndex || oldIndex < 0 || newIndex < 0 || oldIndex >= sections.length || newIndex >= sections.length) {
    return sections;
  }
  return arrayMove(sections, oldIndex, newIndex);
}

/**
 * Moves a top-level question and its nested questions as one block.
 *
 * An attached nested question can move only to a sibling's index under the
 * same parent; this API does not detach or reparent it. For a top-level
 * question, `targetFieldId` sets the destination root index, and a nested
 * target selects its parent block. In one list, the source takes the target's
 * original index (before it when moving up and after it when moving down).
 * Across sections, the source is inserted before the target block. Omit
 * `targetFieldId` to append a top-level block to the target section.
 */
export function moveFormField(sections: FormSection[], fieldId: string, targetSectionId: string, targetFieldId?: string): FormSection[] {
  const source = findFieldLocation(sections, fieldId);
  const targetSectionIndex = sections.findIndex(section => section.id === targetSectionId);
  if (!source || targetSectionIndex === -1) return sections;
  const targetSection = sections[targetSectionIndex]!;
  const target = targetFieldId ? findFieldLocation([targetSection], targetFieldId) : undefined;
  if (targetFieldId && !target) return sections;
  if (source.parentIndex !== undefined) {
    if (source.sectionIndex !== targetSectionIndex || target?.parentIndex !== source.parentIndex) {
      return sections;
    }
    const parent = sections[source.sectionIndex]!.children[source.parentIndex]!;
    const targetIndex = target.fieldIndex;
    if (source.fieldIndex === targetIndex) return sections;
    const nextParent = {
      ...parent,
      children: arrayMove(parent.children ?? [], source.fieldIndex, targetIndex)
    };
    const nextChildren = [...sections[source.sectionIndex]!.children];
    nextChildren[source.parentIndex] = nextParent;
    return replaceSectionChildren(sections, sections[source.sectionIndex]!.id, nextChildren);
  }
  const targetRootIndex = target?.parentIndex ?? target?.fieldIndex;
  if (source.sectionIndex === targetSectionIndex) {
    const nextIndex = targetRootIndex ?? targetSection.children.length - 1;
    if (source.fieldIndex === nextIndex) return sections;
    return replaceSectionChildren(sections, targetSection.id, arrayMove(targetSection.children, source.fieldIndex, nextIndex));
  }
  const sourceSection = sections[source.sectionIndex]!;
  const nextSourceChildren = [...sourceSection.children];
  const [movedField] = nextSourceChildren.splice(source.fieldIndex, 1);
  const nextTargetChildren = [...targetSection.children];
  nextTargetChildren.splice(targetRootIndex ?? nextTargetChildren.length, 0, movedField!);
  return sections.map(section => {
    if (section.id === sourceSection.id) {
      return {
        ...section,
        children: nextSourceChildren
      };
    }
    if (section.id === targetSection.id) {
      return {
        ...section,
        children: nextTargetChildren
      };
    }
    return section;
  });
}

/**
 * Nest a question under an eligible parent in the same section, or promote it
 * back to the root. The structural move and direct-parent condition update are
 * atomic so the UI cannot create a cross-section or indirect dependency.
 */
export function setFormFieldActivationParent(sections: FormSection[], fieldId: string, parentId: string | null): FormSection[] {
  const source = findFieldLocation(sections, fieldId);
  if (!source) return sections;
  if (parentId === null) {
    const promotedField = withoutActivationCondition(source.field);
    if (source.parentIndex === undefined) {
      return replaceField(sections, fieldId, promotedField);
    }
    const section = sections[source.sectionIndex]!;
    const parent = section.children[source.parentIndex]!;
    const nextParent = withFieldChildren(parent, parent.children?.filter(child => child.id !== fieldId) ?? []);
    const nextChildren = [...section.children];
    nextChildren[source.parentIndex] = nextParent;
    nextChildren.splice(source.parentIndex + 1, 0, promotedField);
    return replaceSectionChildren(sections, section.id, nextChildren);
  }
  const parent = findFieldLocation(sections, parentId);
  if (!parent || parent.sectionIndex !== source.sectionIndex || parent.parentIndex !== undefined || parent.field.id === source.field.id || source.field.children?.length || getOnlyActiveWhenOperators(parent.field.fieldType).length === 0) {
    return sections;
  }
  const condition = source.field.onlyActiveWhen?.fieldId === parentId ? source.field.onlyActiveWhen : {
    fieldId: parentId,
    operator: getOnlyActiveWhenOperators(parent.field.fieldType)[0]!,
    candidateValue: null
  };
  const nestedField = {
    ...withFieldChildren(source.field, []),
    onlyActiveWhen: condition
  };
  const section = sections[source.sectionIndex]!;
  const withoutSource = section.children.filter(field => field.id !== fieldId).map(field => withFieldChildren(field, field.children?.filter(child => child.id !== fieldId) ?? []));
  const nextChildren = withoutSource.map(field => field.id === parentId ? {
    ...field,
    children: [...(field.children ?? []), nestedField]
  } : field);
  return replaceSectionChildren(sections, section.id, nextChildren);
}

/** Remove one question, promote its children, and clear references to it. */
export function removeFormField(sections: FormSection[], fieldId: string): FormSection[] {
  const source = findFieldLocation(sections, fieldId);
  if (!source) return sections;
  const nextSections = sections.map((section, sectionIndex) => {
    if (sectionIndex !== source.sectionIndex) return section;
    if (source.parentIndex !== undefined) {
      const nextChildren = section.children.map((field, fieldIndex) => fieldIndex === source.parentIndex ? withFieldChildren(field, field.children?.filter(child => child.id !== fieldId) ?? []) : field);
      return {
        ...section,
        children: nextChildren
      };
    }

    // Independent children can depend on a question other than their visual parent.
    // Clear only references to the removed question below.
    const promotedChildren = source.field.children ?? [];
    const nextChildren = [...section.children];
    nextChildren.splice(source.fieldIndex, 1, ...promotedChildren);
    return {
      ...section,
      children: nextChildren
    };
  });
  return clearActivationReferences(nextSections, new Set([fieldId]));
}

/** Remove a section and clear any legacy references to its question IDs. */
export function removeFormSection(sections: FormSection[], sectionId: string): FormSection[] {
  const removedSection = sections.find(section => section.id === sectionId);
  if (!removedSection) return sections;
  const removedFieldIds = new Set(getFormFields([removedSection]).map(field => field.id));
  return clearActivationReferences(sections.filter(section => section.id !== sectionId), removedFieldIds, new Set([sectionId]));
}
interface FieldLocation {
  sectionIndex: number;
  parentIndex?: number;
  fieldIndex: number;
  field: FormField;
}
function findFieldLocation(sections: FormSection[], fieldId: string): FieldLocation | undefined {
  for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
    const section = sections[sectionIndex]!;
    for (let fieldIndex = 0; fieldIndex < section.children.length; fieldIndex++) {
      const field = section.children[fieldIndex]!;
      if (field.id === fieldId) {
        return {
          sectionIndex,
          fieldIndex,
          field
        };
      }
      const childIndex = field.children?.findIndex(child => child.id === fieldId);
      if (childIndex !== undefined && childIndex !== -1) {
        return {
          sectionIndex,
          parentIndex: fieldIndex,
          fieldIndex: childIndex,
          field: field.children![childIndex]!
        };
      }
    }
  }
  return undefined;
}
function replaceSectionChildren(sections: FormSection[], sectionId: string, children: FormField[]): FormSection[] {
  return sections.map(section => section.id === sectionId ? {
    ...section,
    children
  } : section);
}
function replaceField(sections: FormSection[], fieldId: string, replacement: FormField): FormSection[] {
  return sections.map(section => ({
    ...section,
    children: section.children.map(field => field.id === fieldId ? replacement : field)
  }));
}
function withoutActivationCondition(field: FormField): FormField {
  const {
    onlyActiveWhen: _onlyActiveWhen,
    ...remainingField
  } = field;
  return remainingField;
}
function withFieldChildren(field: FormField, children: FormField[]): FormField {
  const {
    children: _children,
    ...fieldWithoutChildren
  } = field;
  return children.length > 0 ? {
    ...fieldWithoutChildren,
    children
  } : fieldWithoutChildren;
}

/** Clear conditions and dependsOn references to removed questions or sections. */
function clearActivationReferences(sections: FormSection[], removedFieldIds: Set<string>, removedSectionIds = new Set<string>()): FormSection[] {
  return sections.map(section => ({
    ...section,
    children: section.children.map(field => clearFieldActivationReferences(field, removedFieldIds, removedSectionIds))
  }));
}
function withoutRemovedDependencies(field: FormField, removedFieldIds: Set<string>, removedSectionIds: Set<string>): FormField {
  if (!field.dependsOn) return field;
  const {
    dependsOn: _dependsOn,
    ...remainingField
  } = field;
  const fieldIds = field.dependsOn.fieldIds?.filter(id => !removedFieldIds.has(id));
  const sectionIds = field.dependsOn.sectionIds?.filter(id => !removedSectionIds.has(id));
  // An empty dependsOn still matters: section expansion skips questions that
  // declare it. Keep the declaration unless this removal emptied it.
  if (fieldIds?.length === field.dependsOn.fieldIds?.length && sectionIds?.length === field.dependsOn.sectionIds?.length) {
    return field;
  }
  if (!fieldIds?.length && !sectionIds?.length) return remainingField;
  return {
    ...remainingField,
    dependsOn: {
      ...(fieldIds?.length ? {
        fieldIds
      } : {}),
      ...(sectionIds?.length ? {
        sectionIds
      } : {})
    }
  };
}
function clearFieldActivationReferences(field: FormField, removedFieldIds: Set<string>, removedSectionIds = new Set<string>()): FormField {
  const nextField = withoutRemovedDependencies(removedFieldIds.has(field.onlyActiveWhen?.fieldId ?? '') ? withoutActivationCondition(field) : field, removedFieldIds, removedSectionIds);
  return {
    ...nextField,
    ...(nextField.properties ? {
      properties: nextField.properties.map(child => clearFieldActivationReferences(child, removedFieldIds, removedSectionIds))
    } : {}),
    ...(nextField.items ? {
      items: clearFieldActivationReferences(nextField.items, removedFieldIds, removedSectionIds)
    } : {}),
    ...(nextField.children ? {
      children: nextField.children.map(child => clearFieldActivationReferences(child, removedFieldIds, removedSectionIds))
    } : {})
  };
}
function arrayMove<T>(items: T[], fromIndex: number, toIndex: number): T[] {
  const result = [...items];
  const [moved] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, moved!);
  return result;
}
function isTextFieldType(type: FormFieldType): boolean {
  return type === 'shortText' || type === 'longText';
}
function isSelectFieldType(type: FormFieldType): boolean {
  return type === 'singleSelect' || type === 'multiSelect';
}
export function createFormDraftInput(starter: FormStarter): FormDraftInput {
  return {
    name: starter === 'trustReview' ? 'Trust review' : 'New review',
    description: '',
    draftConfig: {
      extractionContext: {
        prompt: ''
      },
      form: starter === 'trustReview' ? createTrustReviewSections() : []
    }
  };
}
export interface PersistedFormRecord {
  id: string;
  name: string;
  description: string;
  draftSchemaVersion: number;
  draftConfig: unknown;
  updatedAt: string | Date;
  currentPublishedVersion?: {
    id: string;
    version: number;
  } | null;
}
export function hydrateFormDraft(form: PersistedFormRecord): FormDraft | undefined {
  const config = readFormConfig(form.draftSchemaVersion, form.draftConfig);
  if (!config) return undefined;
  return {
    id: form.id,
    name: form.name,
    description: form.description,
    draftSchemaVersion: FORM_DRAFT_SCHEMA_VERSION,
    currentPublishedVersion: form.currentPublishedVersion ?? null,
    updatedAt: typeof form.updatedAt === 'string' ? form.updatedAt : form.updatedAt.toISOString(),
    draftConfig: config
  };
}

/** Why a section reference does not include one of its questions. */
export type DependencyExclusionReason = 'self' | 'ownChild' | 'container' | 'hasDependsOn';
export interface SectionDependencyPreview {
  /** Questions the section adds, in form order. */
  included: FormField[];
  excluded: {
    field: FormField;
    reason: DependencyExclusionReason;
  }[];
}

/** Independent questions of a section: top-level questions and grouped children. */
function sectionQuestions(section: FormSection): FormField[] {
  return section.children.flatMap(field => [field, ...(field.children ?? [])]);
}

/**
 * Object and array answers cite graph nodes, not paragraphs, so Platform
 * rejects them on both sides of dependsOn.
 */
function isContainerQuestion(field: FormField): boolean {
  return field.fieldType === 'object' || field.fieldType === 'array';
}

/** Questions that can declare dependsOn: independent and not containers. */
export function canDeclareDependsOn(field: FormField, isStructural: boolean): boolean {
  return !isStructural && !isContainerQuestion(field);
}

/**
 * Shows which questions a section reference from `field` adds. This follows
 * Platform's `Config.DependencyIDs`: a section skips the question itself, its
 * own children, object and array questions, and questions with their own
 * dependsOn.
 */
export function getSectionDependencyPreview(section: FormSection, field: FormField): SectionDependencyPreview {
  const ownChildIds = new Set((field.children ?? []).map(child => child.id));
  const preview: SectionDependencyPreview = {
    included: [],
    excluded: []
  };
  for (const candidate of sectionQuestions(section)) {
    if (candidate.id === field.id) {
      preview.excluded.push({
        field: candidate,
        reason: 'self'
      });
    } else if (ownChildIds.has(candidate.id)) {
      preview.excluded.push({
        field: candidate,
        reason: 'ownChild'
      });
    } else if (isContainerQuestion(candidate)) {
      preview.excluded.push({
        field: candidate,
        reason: 'container'
      });
    } else if (candidate.dependsOn) {
      preview.excluded.push({
        field: candidate,
        reason: 'hasDependsOn'
      });
    } else {
      preview.included.push(candidate);
    }
  }
  return preview;
}

/**
 * Returns the questions whose answers `field` reads, in form order and
 * without duplicates, the same list that Platform sends to extraction.
 */
export function getDependencyQuestionIds(sections: FormSection[], field: FormField): string[] {
  if (!field.dependsOn) return [];
  const explicit = new Set(field.dependsOn.fieldIds ?? []);
  const listedSections = new Set(field.dependsOn.sectionIds ?? []);
  const ids: string[] = [];
  for (const section of sections) {
    const fromSection = listedSections.has(section.id) ? new Set(getSectionDependencyPreview(section, field).included.map(candidate => candidate.id)) : new Set<string>();
    for (const candidate of sectionQuestions(section)) {
      if (explicit.has(candidate.id) || fromSection.has(candidate.id)) {
        ids.push(candidate.id);
      }
    }
  }
  return ids;
}

/** Candidate questions that `field` can list in dependsOn.fieldIds. */
export function getDependencyFieldCandidates(sections: FormSection[], field: FormField): FormField[] {
  const ownChildIds = new Set((field.children ?? []).map(child => child.id));
  return sections.flatMap(sectionQuestions).filter(candidate => candidate.id !== field.id && !ownChildIds.has(candidate.id) && !isContainerQuestion(candidate));
}